import type { JSX } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
  useParams,
  Outlet,
} from "react-router-dom";
import {
  Suspense,
  lazy,
  useEffect,
  useState,
  Component,
  type ReactNode,
} from "react";

import Navbar from "@/layout/Navbar";
import {
  retiredRouteTarget,
  withSearch,
  type RetiredRoute,
} from "@/lib/storefront-trust";
import Footer from "@/layout/Footer";


// Home
const HomePage = lazy(() => import("@/features/home/pages/HomePage"));

// Públicas storefront
const CatalogPage = lazy(() => import("@/features/products/pages/CatalogPage"));
const ProductDetailPage = lazy(() => import("@/features/products/pages/ProductDetailPage"));
const CartPage = lazy(() => import("@/features/cart/pages/CartPage"));
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/features/auth/pages/RegisterPage"));
const VerifyEmailPage = lazy(() => import("@/features/auth/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/features/auth/pages/ResetPasswordPage"));
const ContactPage = lazy(() => import("@/features/legal/pages/ContactPage"));
// Sprint 10 Slice 4 — CMS pages estáticas (/s/:orgSlug/pages/:slug).
const StaticPage = lazy(() => import("@/features/pages/pages/StaticPage"));
const WishlistPage = lazy(() => import("@/features/wishlist/pages/WishlistPage"));

// Checkout
const CheckoutPage = lazy(() => import("@/features/checkout/pages/CheckoutPage"));
const EnzonaReturnPage = lazy(() => import("@/features/checkout/pages/EnzonaReturnPage"));
const EnzonaCancelPage = lazy(() => import("@/features/checkout/pages/EnzonaCancelPage"));
const ThankYouPage = lazy(() => import("@/features/checkout/pages/ThankYouPage"));
const PaymentFailedPage = lazy(() => import("@/features/checkout/pages/PaymentFailedPage"));
const PaymentProcessingPage = lazy(() => import("@/features/checkout/pages/PaymentProcessingPage"));

// Cuenta
import AccountLayout from "@/features/account/layout/AccountLayout";
const ProfilePage = lazy(() => import("@/features/account/pages/ProfilePage"));
const AddressesPage = lazy(() => import("@/features/account/pages/AddressesPage"));
const OrdersPage = lazy(() => import("@/features/account/pages/OrdersPage"));
const OrderDetailPage = lazy(() => import("@/features/account/pages/OrderDetailPage"));
const SecurityPage = lazy(() => import("@/features/account/pages/SecurityPage"));
// Panel Mayorista Slice B — WholesalePage ya no se monta directo en el
// router; vive dentro de WholesalePanelPage (estado RETAIL). Bundlea solo.
const WholesalePanelPage = lazy(
  () => import("@/features/account/pages/WholesalePanelPage"),
);
const ConfirmEmailChangePage = lazy(
  () => import("@/features/account/pages/ConfirmEmailChangePage"),
);
const NotificationsPage = lazy(() => import("@/features/notifications/pages/NotificationsPage"));

// Search
const SearchPage = lazy(() => import("@/features/Search/page/SearchPage"));

// Infra
import {
  api,
  buildStorefrontApiPath,
  resolveStorefrontOrgSlug,
} from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { useAuthBootstrap } from "@/hooks/useAuthBootstrap";
import { useGuestSession } from "@/hooks/useGuestSession";
import { useCartSync } from "@/features/cart/useCartSync";
import ProtectedRoute from "./ProtectedRoute";
import AuthRequiredGate from "./AuthRequiredGate";

import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";

type BootstrapState =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "unavailable" }
  | { status: "error"; message: string };

function extractOrgSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/s\/([^/]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]).trim().toLowerCase() : null;
}

function buildStorefrontBasePath(orgSlug: string | null | undefined) {
  return orgSlug ? `/s/${orgSlug}` : "/";
}

function ScrollToTop() {
  const loc = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [loc.pathname, loc.search]);

  return null;
}

function FullscreenSpinner({ label = "Cargando…" }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="rounded-2xl border border-zinc-800 bg-[var(--card)] px-5 py-4 text-sm opacity-80">
        {label}
      </div>
    </div>
  );
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err: unknown) {
    console.error("Render error:", err);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container py-10">
          <div className="rounded-2xl border border-red-900/40 bg-red-900/10 p-5">
            <h2 className="text-lg font-semibold">Algo salió mal 😵‍💫</h2>
            <p className="text-sm opacity-80">Intenta recargar la página.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function GuestOnly({ children }: { children: JSX.Element }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);

  const loc = useLocation();
  const orgSlug =
    extractOrgSlugFromPath(loc.pathname) || resolveStorefrontOrgSlug();
  const basePath = buildStorefrontBasePath(orgSlug);

  if (!hydrated) {
    return <FullscreenSpinner label="Comprobando sesión…" />;
  }

  if (accessToken && user) {
    const params = new URLSearchParams(loc.search);
    const next = params.get("next") || basePath;
    return <Navigate to={next} replace />;
  }

  return children;
}

function LegacyOrderRedirect() {
  const { id, orgSlug } = useParams<{ id: string; orgSlug?: string }>();
  const resolved = orgSlug || resolveStorefrontOrgSlug();
  const target = resolved ? `/s/${resolved}/account/orders/${id}` : "/";
  return <Navigate to={target} replace />;
}

function RedirectToStorefront({ suffix = "" }: { suffix?: string }) {
  const { search } = useLocation();
  const orgSlug = resolveStorefrontOrgSlug();
  const target = orgSlug ? `/s/${orgSlug}${suffix}` : "/";
  return <Navigate to={withSearch(target, search)} replace />;
}

// Slice 11 — rutas cuya página era una plantilla falsa. Dentro o fuera de
// /s/:orgSlug, un solo salto al destino con prefijo de tienda y query string.
function RetiredRouteRedirect({ route }: { route: RetiredRoute }) {
  const { search } = useLocation();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const resolved = orgSlug || resolveStorefrontOrgSlug();
  if (!resolved) return <Navigate to="/" replace />;
  return <Navigate to={retiredRouteTarget(resolved, route, search)} replace />;
}

function StoreUnavailable({
  orgSlug,
  message,
}: {
  orgSlug: string;
  message?: string;
}) {
  return (
    <div className="container py-10">
      <div className="rounded-2xl border border-zinc-800 bg-[var(--card)] p-6 text-center">
        <h2 className="text-xl font-semibold">Storefront no disponible</h2>
        <p className="mt-2 text-sm opacity-70">
          {message || `La tienda ${orgSlug} no está disponible ahora mismo.`}
        </p>
      </div>
    </div>
  );
}

function NotFoundPage() {
  const orgSlug = resolveStorefrontOrgSlug();
  const storeCatalogHref = orgSlug ? `/s/${orgSlug}/tienda` : "/";

  return (
    <div className="container py-10">
      <div className="rounded-2xl border border-zinc-800 bg-[var(--card)] p-6 text-center">
        <h2 className="text-xl font-semibold">No encontrado</h2>
        <p className="text-sm opacity-70">La ruta que buscas no existe.</p>
        <Link
          className="mt-3 inline-block text-sm underline"
          to={storeCatalogHref}
        >
          Volver al catálogo
        </Link>
      </div>
    </div>
  );
}

function StorefrontTenantLayout() {
  const { orgSlug = "" } = useParams<{ orgSlug: string }>();
  const normalizedOrgSlug = orgSlug.trim().toLowerCase();

  const [state, setState] = useState<BootstrapState>({ status: "loading" });

  useEffect(() => {
    let active = true;

    (async () => {
      setState({ status: "loading" });

      try {
        await api.get(buildStorefrontApiPath("/bootstrap", normalizedOrgSlug));
        if (!active) return;
        setState({ status: "ready" });
      } catch (error: any) {
        if (!active) return;

        const status = error?.response?.status;
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "No se pudo cargar la tienda.";

        if (status === 404) {
          setState({ status: "unavailable" });
          return;
        }

        setState({ status: "error", message });
      }
    })();

    return () => {
      active = false;
    };
  }, [normalizedOrgSlug]);

  if (state.status === "loading") {
    return <FullscreenSpinner label="Cargando tienda…" />;
  }

  if (state.status === "unavailable") {
    return <StoreUnavailable orgSlug={normalizedOrgSlug} />;
  }

  if (state.status === "error") {
    return (
      <StoreUnavailable orgSlug={normalizedOrgSlug} message={state.message} />
    );
  }

  return (
    <>
      <EmailVerificationBanner />
      <Outlet />
    </>
  );
}

export default function AppRouter() {
  useAuthBootstrap();
  useGuestSession(); // Garantiza guestSessionId en localStorage para cart sync
  useCartSync();

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Navbar />

      <div className="flex-1">
        <ErrorBoundary>
          <Suspense fallback={<FullscreenSpinner />}>
            <Routes>
              <Route
                path="/"
                element={
                  resolveStorefrontOrgSlug() ? (
                    <RedirectToStorefront />
                  ) : (
                    <HomePage />
                  )
                }
              />

              <Route path="/s/:orgSlug" element={<StorefrontTenantLayout />}>
                <Route index element={<HomePage />} />
                <Route path="tienda" element={<CatalogPage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="product/:slug" element={<ProductDetailPage />} />
                <Route path="cart" element={<CartPage />} />

                <Route
                  path="login"
                  element={
                    <GuestOnly>
                      <LoginPage />
                    </GuestOnly>
                  }
                />
                <Route
                  path="register"
                  element={
                    <GuestOnly>
                      <RegisterPage />
                    </GuestOnly>
                  }
                />

                {/* Sprint 5 Slice 2 — flows email-driven (acceso por link sin sesión) */}
                <Route path="verify-email" element={<VerifyEmailPage />} />
                <Route
                  path="forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path="reset-password"
                  element={<ResetPasswordPage />}
                />
                {/* Sprint 5 Slice 5 — confirm email change (acceso por link al NUEVO email) */}
                <Route
                  path="account/confirm-email-change"
                  element={<ConfirmEmailChangePage />}
                />

                <Route
                  path="wishlist"
                  element={
                    <AuthRequiredGate
                      title="Inicia sesión para ver tus favoritos"
                      description="Guarda productos que te gusten para comprarlos después."
                    >
                      <WishlistPage />
                    </AuthRequiredGate>
                  }
                />

                {/* Slice 11 — plantillas falsas retiradas (Stripe, 30 días,
                    horarios inventados): términos → CMS; el resto → inicio. */}
                <Route
                  path="legal/terms"
                  element={<RetiredRouteRedirect route="/legal/terms" />}
                />
                <Route
                  path="legal/privacy"
                  element={<RetiredRouteRedirect route="/legal/privacy" />}
                />
                <Route path="contact" element={<ContactPage />} />
                <Route
                  path="help"
                  element={<RetiredRouteRedirect route="/help" />}
                />
                {/* Sprint 10 Slice 4 — CMS pages whitelisted slugs. */}
                <Route path="pages/:slug" element={<StaticPage />} />

                <Route
                  path="checkout"
                  element={
                    <ProtectedRoute>
                      <CheckoutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="checkout/enzona/return"
                  element={<EnzonaReturnPage />}
                />
                <Route
                  path="checkout/enzona/cancel"
                  element={<EnzonaCancelPage />}
                />
                <Route
                  path="checkout/thank-you"
                  element={<ThankYouPage />}
                />
                <Route
                  path="checkout/payment-failed"
                  element={<PaymentFailedPage />}
                />
                <Route
                  path="checkout/payment-processing"
                  element={<PaymentProcessingPage />}
                />

                <Route
                  path="account"
                  element={
                    <ProtectedRoute>
                      <AccountLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="profile" replace />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="addresses" element={<AddressesPage />} />
                  <Route path="orders" element={<OrdersPage />} />
                  <Route path="orders/:id" element={<OrderDetailPage />} />
                  <Route path="security" element={<SecurityPage />} />
                  {/*
                    Panel Mayorista Slice B — la solicitud/estado se movió a
                    /wholesale (top-level). Mantenemos redirect para no
                    romper bookmarks viejos del tab que vivía acá adentro.
                  */}
                  <Route
                    path="wholesale"
                    element={<Navigate to="../../wholesale" replace />}
                  />
                </Route>

                {/*
                  Panel Mayorista Slice B — ruta top-level para el panel
                  mayorista. NO usa ProtectedRoute porque la propia página
                  maneja los 3 estados (no-auth / RETAIL / WHOLESALE) con
                  CTAs propios cuando corresponde.
                */}
                <Route
                  path="wholesale"
                  element={<WholesalePanelPage />}
                />

                <Route
                  path="notifications"
                  element={
                    <AuthRequiredGate
                      title="Inicia sesión para ver notificaciones"
                      description="Recibe actualizaciones sobre tus pedidos y ofertas."
                    >
                      <NotificationsPage />
                    </AuthRequiredGate>
                  }
                />

                <Route
                  path="me"
                  element={<Navigate to="../account/profile" replace />}
                />
                <Route
                  path="orders"
                  element={<Navigate to="../account/orders" replace />}
                />
                <Route path="orders/:id" element={<LegacyOrderRedirect />} />
                <Route path="admin/*" element={<RedirectToStorefront />} />
                <Route path="support/*" element={<RedirectToStorefront />} />
              </Route>

              {/* redirects legacy */}
              <Route
                path="/tienda"
                element={<RedirectToStorefront suffix="/tienda" />}
              />
              <Route
                path="/search"
                element={<RedirectToStorefront suffix="/search" />}
              />
              <Route path="/product/:slug" element={<RedirectToStorefront />} />
              <Route
                path="/cart"
                element={<RedirectToStorefront suffix="/cart" />}
              />
              <Route
                path="/login"
                element={<RedirectToStorefront suffix="/login" />}
              />
              <Route
                path="/register"
                element={<RedirectToStorefront suffix="/register" />}
              />
              <Route
                path="/wishlist"
                element={<RedirectToStorefront suffix="/wishlist" />}
              />
              <Route
                path="/checkout"
                element={<RedirectToStorefront suffix="/checkout" />}
              />
              <Route
                path="/account"
                element={<RedirectToStorefront suffix="/account/profile" />}
              />
              <Route
                path="/account/profile"
                element={<RedirectToStorefront suffix="/account/profile" />}
              />
              <Route
                path="/account/addresses"
                element={<RedirectToStorefront suffix="/account/addresses" />}
              />
              <Route
                path="/account/orders"
                element={<RedirectToStorefront suffix="/account/orders" />}
              />
              <Route
                path="/account/orders/:id"
                element={<LegacyOrderRedirect />}
              />
              <Route
                path="/account/security"
                element={<RedirectToStorefront suffix="/account/security" />}
              />
              <Route
                path="/notifications"
                element={<RedirectToStorefront suffix="/notifications" />}
              />
              <Route
                path="/legal/terms"
                element={<RetiredRouteRedirect route="/legal/terms" />}
              />
              <Route
                path="/legal/privacy"
                element={<RetiredRouteRedirect route="/legal/privacy" />}
              />
              <Route
                path="/contact"
                element={<RedirectToStorefront suffix="/contact" />}
              />
              <Route
                path="/help"
                element={<RetiredRouteRedirect route="/help" />}
              />
              <Route
                path="/me"
                element={<RedirectToStorefront suffix="/account/profile" />}
              />
              <Route
                path="/orders"
                element={<RedirectToStorefront suffix="/account/orders" />}
              />
              <Route path="/orders/:id" element={<LegacyOrderRedirect />} />

              {/*
                Slice 11 — `/admin/*` y `/support/*` ya no existen en la
                storefront (el panel vive en apps/web; soporte se borró en el
                Slice 9) → inicio de la tienda.
              */}
              <Route path="/admin/*" element={<RedirectToStorefront />} />
              <Route path="/support/*" element={<RedirectToStorefront />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>

      <Footer />
    </div>
  );
}
